import { useCallback, useMemo, useState } from 'react';
import { type LearnerProject } from 'LearnerProjects';
import { ProjectSubmissionFormModal } from 'ProjectSubmission';
import { ProjectGroupSetupModal, ViewProjectGroupModal } from 'ProjectGroup';
import { useCurrentUser } from 'FrontRoyalAngular';
import { ProjectContentBoxPresentation } from './ProjectContentBox';
import { useProjectImage } from './useProjectImage';

export function CapstoneProjectBox({
    project,
    fullWidth,
    padding = '2.5',
    titleSize = 'sm',
}: {
    project: LearnerProject;
    fullWidth: boolean;
    padding?: '2.5' | '3.5';
    titleSize?: 'base' | 'xs' | 'sm';
}) {
    const imageSrc = useProjectImage();
    const currentUser = useCurrentUser();
    const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
    const [isGroupSetupModalOpen, setIsGroupSetupModalOpen] = useState(false);
    const [isViewGroupModalOpen, setIsViewGroupModalOpen] = useState(false);

    const projectGroupMembership = useMemo(
        () =>
            (currentUser?.acceptedProjectGroupMemberships || []).find(
                m => m.requirementIdentifier === project.requirementIdentifier,
            ) || null,
        [currentUser, project.requirementIdentifier],
    );

    const handlePress = useCallback(() => {
        setIsSubmissionModalOpen(true);
    }, []);

    const handleCloseSubmissionModal = useCallback(() => {
        setIsSubmissionModalOpen(false);
    }, []);

    const handleCreateGroup = useCallback(() => {
        setIsSubmissionModalOpen(false);
        setIsGroupSetupModalOpen(true);
    }, []);

    const handleCloseGroupSetup = useCallback(() => {
        setIsGroupSetupModalOpen(false);
    }, []);

    const handleViewGroup = useCallback(() => {
        setIsSubmissionModalOpen(false);
        setIsViewGroupModalOpen(true);
    }, []);

    const handleCloseViewGroup = useCallback(() => {
        setIsViewGroupModalOpen(false);
    }, []);

    return (
        <>
            <ProjectContentBoxPresentation
                title={project.title}
                src={imageSrc}
                fullWidth={fullWidth}
                padding={padding}
                titleSize={titleSize}
                projectRequirementIdentifier={project.requirementIdentifier}
                itemId={project.id}
                initiallyExpanded={false}
                expandedContent={null}
                onPress={handlePress}
            />
            {isSubmissionModalOpen && (
                <ProjectSubmissionFormModal
                    isOpen={isSubmissionModalOpen}
                    closeModal={handleCloseSubmissionModal}
                    project={project}
                    projectGroupMembership={projectGroupMembership}
                    onCreateGroup={handleCreateGroup}
                    onViewGroup={handleViewGroup}
                />
            )}
            {isGroupSetupModalOpen && (
                <ProjectGroupSetupModal
                    isOpen={isGroupSetupModalOpen}
                    project={project}
                    closeModal={handleCloseGroupSetup}
                />
            )}
            {isViewGroupModalOpen && (
                <ViewProjectGroupModal
                    isOpen={isViewGroupModalOpen}
                    project={project}
                    closeModal={handleCloseViewGroup}
                    projectGroupMembership={projectGroupMembership}
                />
            )}
        </>
    );
}
